import { Box, ClickAwayListener, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import type { JSX, PropsWithChildren } from "react";
import { useState } from "react";
import PencilNotFilledIcon from "@entrilia/shared/icons/PencilNotFilledIcon";
import { useFieldValuesContext } from "../FieldValuesContext";

interface DisplayValueProps {
  value: string;
  component?: JSX.Element | JSX.Element[] | null;
}

const DisplayValue = ({ value, component }: DisplayValueProps) => (
  <Box sx={{ px: 1.5, flexGrow: 1, display: "flex", alignItems: "center", minHeight: "2.5em" }}>
    {component ? component : <Typography>{value}</Typography>}
  </Box>
);

interface Props {
  id?: string;
  displayValue: string;
  isEdit: boolean;
  onEditChange?: (isEdit: boolean) => void;
  isReadonly?: boolean;
  readonlyReason?: string;
  displayValueComponent?: JSX.Element | JSX.Element[] | null;
  displayHoverComponent?: JSX.Element | JSX.Element[] | null;
  onLeaveEditMode?: () => Promise<boolean>;
  emptyPlaceholder?: string;
  editViaDialog?: boolean;
  onEditDialogOpen?: () => void;
}

const FieldValueWrapper = ({
  id,
  isEdit,
  onEditChange,
  isReadonly,
  readonlyReason,
  displayValue,
  displayValueComponent,
  displayHoverComponent,
  onLeaveEditMode,
  emptyPlaceholder,
  editViaDialog,
  onEditDialogOpen,
  children,
}: PropsWithChildren<Props>) => {
  const { fieldValuesState, updateFieldValuesState, showFieldValueBorders } = useFieldValuesContext();

  const [isHovered, setIsHovered] = useState(false);

  const handlePlaceholderClick = () => {
    if (editViaDialog && onEditDialogOpen) {
      onEditDialogOpen();
      return;
    }

    // Do not allow multiple fields to be edited if validation is not passed
    if (fieldValuesState.isValid) {
      onEditChange?.(true);
      if (fieldValuesState.fieldEditState !== "editing") {
        updateFieldValuesState({ fieldEditState: "editing" });
      }
    }
  };

  const handleLeaveEditMode = async () => {
    if (!isEdit) {
      return;
    }

    const isValid = await onLeaveEditMode?.();
    if (isValid === false) {
      return;
    }
    onEditChange?.(false);
  };

  if (isReadonly) {
    return (
      <Tooltip title={readonlyReason ?? ""} arrow describeChild>
        <Stack
          direction="row"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{ alignItems: "center", justifyContent: "space-between", width: "100%", pr: 4 }}
        >
          <DisplayValue value={displayValue} component={displayValueComponent} />
          {displayValue && isHovered && displayHoverComponent && (
            <Box onClick={(e) => e.stopPropagation()} sx={{ mr: 1, display: "flex" }}>
              {displayHoverComponent}
            </Box>
          )}
        </Stack>
      </Tooltip>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleLeaveEditMode}>
      <Paper
        id={id}
        elevation={0}
        sx={(t) => ({
          width: "100%",
          minHeight: t.spacing(4.5),
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          border: showFieldValueBorders && !isEdit ? `1px solid ${t.palette.divider}` : undefined,
          "&:hover": {
            backgroundColor: isHovered && !isEdit ? t.palette.action.hover : "transparent",
          },
        })}
        onClick={handlePlaceholderClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          {!isEdit && displayValue && <DisplayValue value={displayValue} component={displayValueComponent} />}

          {displayValue && isHovered && !isEdit && (
            <>
              {displayHoverComponent && (
                <Box onClick={(e) => e.stopPropagation()} sx={{ mr: 1, display: "flex" }}>
                  {displayHoverComponent}
                </Box>
              )}
              <IconButton onClick={handlePlaceholderClick}>
                <PencilNotFilledIcon />
              </IconButton>
            </>
          )}

          {!displayValue && isHovered && !isEdit && (
            <Typography color="textSecondary" sx={{ px: 1.5 }}>
              {emptyPlaceholder || "Add ..."}
            </Typography>
          )}

          {isEdit && children}
        </Box>
      </Paper>
    </ClickAwayListener>
  );
};

export default FieldValueWrapper;
